import { useRef, useState, useEffect, useMemo } from "react";
import { UploadSimple, TrashSimple, Table, ClipboardText, FilePlus, StackSimple, PencilSimple, Funnel } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fileColor } from "../lib/files";
import { ColumnFilter } from "./ColumnFilter";

function PasteDialog({ jobId, onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const job = await api.paste(jobId, name, text);
      onDone(job);
      setOpen(false);
      setText(""); setName("");
      toast.success("Dati incollati importati");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore importazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-paste-dialog-btn" className="gap-2">
          <ClipboardText size={16} /> Incolla dati
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-popover border-border max-w-2xl">
        <DialogHeader><DialogTitle>Incolla dati tabellari</DialogTitle>
          <DialogDescription>Incolla una tabella TSV o CSV; la prima riga sarà usata come intestazioni.</DialogDescription>
        </DialogHeader>
        <Input placeholder="Nome tabella (opzionale)" value={name} onChange={(e) => setName(e.target.value)}
          data-testid="paste-name-input" className="bg-muted" />
        <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Incolla qui (TSV o CSV, prima riga = intestazioni)..."
          data-testid="paste-text-input" className="bg-muted grid-font text-sm" />
        <DialogFooter>
          <Button onClick={submit} disabled={loading} data-testid="paste-submit-btn">
            {loading ? "Importo..." : "Importa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SheetDialog({ prompt, onPick, onCancel }) {
  const [sheet, setSheet] = useState(prompt?.sheets?.[0] || "");
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="bg-popover border-border max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><StackSimple size={20} /> Scegli il foglio</DialogTitle>
          <DialogDescription>Il file "{prompt.file.name}" contiene più fogli. Quale vuoi importare?</DialogDescription>
        </DialogHeader>
        <Select value={sheet} onValueChange={setSheet}>
          <SelectTrigger className="bg-muted" data-testid="sheet-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            {prompt.sheets.map((s) => <SelectItem key={s} value={s} data-testid={`sheet-opt-${s}`}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button onClick={() => onPick(sheet)} data-testid="sheet-confirm-btn">Importa foglio</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SourceFilterDialog({ jobId, file, onSaved, onClose }) {
  const [flt, setFlt] = useState(file.row_filter || {});
  const [dedup, setDedup] = useState(() => ({ on: !!file.dedup?.on, columns: file.dedup?.columns || [] }));
  const setColFilter = (key, vals) => setFlt((p) => { const n = { ...p }; if (!vals || !vals.length) delete n[key]; else n[key] = vals; return n; });
  const toggleDedupCol = (c) => setDedup((d) => ({ ...d, columns: d.columns.includes(c) ? d.columns.filter((x) => x !== c) : [...d.columns, c] }));
  const norm = (v) => (v === null || v === undefined ? "" : String(v));
  const rows = file.rows || [];
  const fltKey = JSON.stringify(flt);
  const filtered = useMemo(() => {
    const fentries = Object.entries(flt).filter(([, v]) => Array.isArray(v) && v.length);
    if (!fentries.length) return rows;
    return rows.filter((r) => fentries.every(([k, vals]) => vals.includes(norm(r[k]))));
  }, [rows, fltKey]);
  const dedupKey = JSON.stringify(dedup);
  const dedupped = useMemo(() => {
    if (!dedup.on) return filtered;
    const cols = dedup.columns.length ? dedup.columns : file.columns;
    const seen = new Set(); const out = [];
    for (const r of filtered) { const k = cols.map((c) => norm(r[c])).join("||"); if (seen.has(k)) continue; seen.add(k); out.push(r); }
    return out;
  }, [filtered, dedupKey]);
  const removedDup = filtered.length - dedupped.length;
  const color = fileColor(file.slot);
  const save = async () => {
    try {
      const updated = await api.setFileFilter(jobId, file.slot, flt, dedup);
      onSaved(updated);
      toast.success(`Applicato: ${dedupped.length}/${rows.length} righe${dedup.on ? ` (${removedDup} duplicati rimossi)` : ""}`);
      onClose();
    } catch (e) { toast.error("Errore applicazione filtro"); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-popover border-border max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color }}><Funnel size={20} weight="fill" /> Filtra sorgente · {file.name}</DialogTitle>
          <DialogDescription>Filtra le righe per valore (stile Excel) e/o rimuovi i duplicati. Solo i dati risultanti vengono usati nell'abbinamento e nei conteggi.</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-border p-3 bg-muted/40 space-y-2" data-testid="source-dedup-box">
          <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
            <Checkbox checked={dedup.on} onCheckedChange={(v) => setDedup((d) => ({ ...d, on: !!v }))} data-testid="source-dedup-toggle" />
            Rimuovi righe duplicate
            {dedup.on && <span className="text-xs font-normal text-muted-foreground">· {removedDup} duplicati rimossi</span>}
          </label>
          {dedup.on && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Considera duplicate le righe con gli stessi valori in: {dedup.columns.length ? "" : "tutte le colonne"}</div>
              <div className="flex flex-wrap gap-1" data-testid="source-dedup-columns">
                {file.columns.map((c) => (
                  <button key={c} onClick={() => toggleDedupCol(c)} data-testid={`source-dedup-col-${c}`}
                    className="text-[11px] px-2 py-0.5 rounded-full border transition-colors"
                    style={dedup.columns.includes(c) ? { background: color, color: "#fff", borderColor: color } : { borderColor: "hsl(var(--border))", color: "hsl(var(--muted-foreground))" }}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="text-xs font-semibold" data-testid="source-filter-count">{dedupped.length} / {rows.length} righe dopo filtro{dedup.on ? " + deduplica" : ""}</div>
        <div className="overflow-auto max-h-[46vh] border border-border rounded-lg">
          <table className="text-xs grid-font" data-testid="source-filter-table">
            <thead>
              <tr>
                {file.columns.map((c) => (
                  <th key={c} className="p-1 bg-secondary sticky top-0 text-left" style={{ color }}>
                    <div className="flex items-center gap-1">
                      <span>{c}</span>
                      <ColumnFilter colKey={c} baseRows={rows} allFilters={flt} selected={flt[c]} onChange={(v) => setColFilter(c, v)} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dedupped.slice(0, 100).map((r, ri) => (
                <tr key={ri} data-testid={`source-filter-row-${ri}`}>{file.columns.map((c) => <td key={c} className="px-2 py-0.5 border border-border/40 data-cell">{norm(r[c])}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
        {dedupped.length > 100 && <p className="text-xs text-muted-foreground">Anteprima limitata a 100 righe.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => { setFlt({}); setDedup({ on: false, columns: [] }); }} data-testid="source-filter-clear">Azzera tutto</Button>
          <Button onClick={save} data-testid="source-filter-save">Applica</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FileCard({ file, onDelete, onRename, onFilter }) {
  const color = fileColor(file.slot);
  const _active = Object.entries(file.row_filter || {}).filter(([, v]) => Array.isArray(v) && v.length);
  const _dedup = file.dedup && file.dedup.on;
  const _norm = (v) => (v === null || v === undefined ? "" : String(v));
  const filteredCount = useMemo(() => {
    let rows = file.rows || [];
    if (_active.length) rows = rows.filter((r) => _active.every(([k, vals]) => vals.includes(_norm(r[k]))));
    if (_dedup) {
      const cols = (file.dedup.columns && file.dedup.columns.length) ? file.dedup.columns : file.columns;
      const seen = new Set(); const out = [];
      for (const r of rows) { const k = cols.map((c) => _norm(r[c])).join("||"); if (seen.has(k)) continue; seen.add(k); out.push(r); }
      rows = out;
    }
    return rows.length;
  }, [file]);
  const _changed = _active.length || _dedup;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(file.name);
  useEffect(() => setName(file.name), [file.name]);
  const save = () => {
    const v = name.trim();
    setEditing(false);
    if (v && v !== file.name) onRename(file.slot, v);
    else setName(file.name);
  };
  return (
    <div className="metallic rounded-xl border border-border p-4 fade-up" data-testid={`file-card-${file.slot}`}
      style={{ borderLeft: `3px solid ${color}` }}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Table size={20} weight="duotone" style={{ color }} />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">File {file.slot}</div>
            {editing ? (
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onBlur={save}
                onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setName(file.name); setEditing(false); } }}
                data-testid={`file-name-input-${file.slot}`}
                className="font-semibold bg-muted border border-border rounded px-1 py-0.5 text-sm w-full outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]" />
            ) : (
              <button onClick={() => setEditing(true)} data-testid={`file-name-${file.slot}`}
                className="font-semibold flex items-center gap-1 hover:text-primary transition-colors max-w-full" title="Rinomina">
                <span className="truncate">{file.name}</span>
                <PencilSimple size={13} className="opacity-50 shrink-0" />
              </button>
            )}
          </div>
        </div>
        <button onClick={() => onDelete(file.slot)} data-testid={`delete-file-${file.slot}-btn`}
          className="text-muted-foreground hover:text-destructive transition-colors shrink-0 ml-2">
          <TrashSimple size={18} />
        </button>
      </div>
      <div className="mt-3 flex gap-2 text-xs items-center flex-wrap">
        <span className="grid-font px-2 py-1 rounded" style={{ background: `${color}22`, color }}>{_changed ? `${filteredCount} / ${file.rows.length}` : file.rows.length} righe</span>
        <span className="grid-font px-2 py-1 rounded bg-muted text-muted-foreground">{file.columns.length} colonne</span>
        {_active.length > 0 && (
          <span className="grid-font px-2 py-1 rounded flex items-center gap-1" style={{ background: `${color}22`, color }} data-testid={`file-filter-active-${file.slot}`}><Funnel size={11} weight="fill" /> filtrato</span>
        )}
        {_dedup && (
          <span className="grid-font px-2 py-1 rounded flex items-center gap-1" style={{ background: `${color}22`, color }} data-testid={`file-dedup-active-${file.slot}`}>deduplicato</span>
        )}
        <button onClick={() => onFilter(file)} data-testid={`file-filter-btn-${file.slot}`}
          className="ml-auto flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"><Funnel size={13} /> Filtra</button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {file.columns.slice(0, 6).map((c) => (
          <span key={c} className="grid-font text-[10px] px-1.5 py-0.5 rounded bg-secondary/60">{c}</span>
        ))}
        {file.columns.length > 6 && <span className="text-[10px] text-muted-foreground">+{file.columns.length - 6}</span>}
      </div>
    </div>
  );
}

function AutoText({ value, onChange, className, style, testid }) {
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } }, [value, style?.width]);
  return <textarea ref={ref} rows={1} value={value} onChange={onChange} data-testid={testid} style={{ ...style, whiteSpace: "pre-wrap", overflowWrap: "anywhere", overflow: "hidden" }}
    className={`resize-none block leading-snug ${className}`} />;
}

function PdfPreviewDialog({ data, onConfirm, onCancel }) {
  const tables = data.tables && data.tables.length ? data.tables : [{ columns: data.columns || [], rows: data.rows || [] }];
  const [ti, setTi] = useState(0);
  const [name, setName] = useState(data.name?.replace(/\.pdf$/i, "") || "Tabella PDF");
  const [colW, setColW] = useState({});
  const [cols, setCols] = useState(tables[0].columns || []);
  const [grid, setGrid] = useState(() => (tables[0].rows || []).map((r) => (tables[0].columns || []).map((c) => r[c] ?? "")));

  useEffect(() => {
    const t = tables[ti] || { columns: [], rows: [] };
    setCols(t.columns || []);
    setGrid((t.rows || []).map((r) => (t.columns || []).map((c) => r[c] ?? "")));
    setColW({});
  }, [ti]);

  const startColResize = (e, ci) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX; const sw = colW[ci] || 112;
    document.body.style.userSelect = "none";
    const mv = (ev) => setColW((w) => ({ ...w, [ci]: Math.max(60, sw + ev.clientX - sx) }));
    const up = () => { document.body.style.userSelect = ""; window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", mv); window.addEventListener("mouseup", up);
  };
  const setCell = (ri, ci, v) => setGrid((g) => g.map((row, i) => i === ri ? row.map((x, j) => j === ci ? v : x) : row));
  const setCol = (ci, v) => setCols((cs) => cs.map((x, j) => j === ci ? v : x));
  const addRow = () => setGrid((g) => [...g, cols.map(() => "")]);
  const delRow = (ri) => setGrid((g) => g.filter((_, i) => i !== ri));
  const addCol = () => { setCols((cs) => [...cs, `col${cs.length + 1}`]); setGrid((g) => g.map((row) => [...row, ""])); };
  const delCol = (ci) => { setCols((cs) => cs.filter((_, j) => j !== ci)); setGrid((g) => g.map((row) => row.filter((_, j) => j !== ci))); };
  const mergeCol = (ci) => {
    if (ci >= cols.length - 1) return;
    setCols((cs) => cs.map((x, j) => j === ci ? `${x} ${cs[ci + 1]}`.trim() : x).filter((_, j) => j !== ci + 1));
    setGrid((g) => g.map((row) => row.map((x, j) => j === ci ? `${x} ${row[ci + 1] ?? ""}`.trim() : x).filter((_, j) => j !== ci + 1)));
  };
  const splitCol = (ci) => {
    const sp = (s) => { const str = String(s ?? ""); const i = str.indexOf(" "); return i < 0 ? [str, ""] : [str.slice(0, i), str.slice(i + 1).trim()]; };
    setCols((cs) => { const n = [...cs]; n.splice(ci + 1, 0, `${cs[ci]}_2`); return n; });
    setGrid((g) => g.map((row) => { const [l, r] = sp(row[ci]); const n = [...row]; n[ci] = l; n.splice(ci + 1, 0, r); return n; }));
  };
  const confirm = () => onConfirm({ name, columns: cols, rows: grid.map((row) => Object.fromEntries(cols.map((c, j) => [c, row[j]]))) });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="bg-popover border-border max-w-5xl">
        <DialogHeader><DialogTitle>Anteprima tabella PDF</DialogTitle>
          <DialogDescription>Scegli la tabella, correggi celle/intestazioni, unisci o dividi colonne, poi conferma.</DialogDescription>
        </DialogHeader>
        {tables.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="pdf-table-selector">
            {tables.map((t, i) => (
              <button key={i} onClick={() => setTi(i)} data-testid={`pdf-table-tab-${i}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${ti === i ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                Tabella {i + 1} · {(t.rows || []).length} righe × {(t.columns || []).length} col
              </button>
            ))}
          </div>
        )}
        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="pdf-name-input" className="bg-muted" placeholder="Nome tabella" />
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={addRow} data-testid="pdf-add-row-btn">+ Riga</Button>
          <Button size="sm" variant="outline" onClick={addCol} data-testid="pdf-add-col-btn">+ Colonna</Button>
          <span className="text-xs text-muted-foreground self-center ml-auto">{grid.length} righe · {cols.length} colonne</span>
        </div>
        <div className="overflow-auto max-h-[52vh] border border-border rounded-lg">
          <table className="text-xs grid-font" data-testid="pdf-preview-table">
            <thead>
              <tr>
                <th className="p-1 bg-secondary sticky top-0" />
                {cols.map((c, ci) => (
                  <th key={ci} className="p-1 bg-secondary sticky top-0" style={{ position: "relative" }}>
                    <div className="flex items-start gap-1">
                      <AutoText value={c} onChange={(e) => setCol(ci, e.target.value)} testid={`pdf-col-${ci}`}
                        style={{ width: colW[ci] || 112 }}
                        className="bg-muted border border-border rounded px-1 py-0.5 font-semibold" />
                      <button onClick={() => splitCol(ci)} data-testid={`pdf-split-col-${ci}`} title="Dividi colonna al primo spazio" className="text-[hsl(var(--accent))] px-0.5">⑃</button>
                      {ci < cols.length - 1 && <button onClick={() => mergeCol(ci)} data-testid={`pdf-merge-col-${ci}`} title="Unisci con la colonna a destra" className="text-[hsl(var(--accent))] px-0.5">⇥</button>}
                      <button onClick={() => delCol(ci)} data-testid={`pdf-del-col-${ci}`} className="text-destructive px-1">×</button>
                    </div>
                    <div onMouseDown={(e) => startColResize(e, ci)} data-testid={`pdf-col-resize-${ci}`}
                      className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-[hsl(var(--accent))]" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.map((row, ri) => (
                <tr key={ri}>
                  <td className="p-1 align-top"><button onClick={() => delRow(ri)} data-testid={`pdf-del-row-${ri}`} className="text-destructive px-1">×</button></td>
                  {row.map((v, ci) => (
                    <td key={ci} className="p-0.5 border border-border/50 align-top">
                      <AutoText value={v} onChange={(e) => setCell(ri, ci, e.target.value)} testid={`pdf-cell-${ri}-${ci}`}
                        style={{ width: colW[ci] || 112 }}
                        className="bg-transparent px-1 py-0.5 outline-none" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="pdf-cancel-btn">Annulla</Button>
          <Button onClick={confirm} data-testid="pdf-confirm-btn" disabled={!cols.length || !grid.length}>Importa tabella</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DataSources({ job, setJob }) {
  const inputRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sheetPrompt, setSheetPrompt] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [filterFile, setFilterFile] = useState(null);
  const canAdd = job.files.length < 3;

  const doUpload = async (fileObj, sheet) => {
    if (!fileObj) return;
    if (fileObj.name && fileObj.name.toLowerCase().endsWith(".pdf")) {
      setUploading(true);
      try {
        const prev = await api.pdfPreview(job.id, fileObj);
        setPdfPreview(prev);
      } catch (e) { toast.error(e.response?.data?.detail || "Errore lettura PDF"); }
      finally { setUploading(false); }
      return;
    }
    setUploading(true);
    try {
      const res = await api.uploadFile(job.id, fileObj, sheet);
      if (res.needs_sheet) { setSheetPrompt({ file: fileObj, sheets: res.sheets }); return; }
      setJob(res);
      setSheetPrompt(null);
      toast.success(`${fileObj.name} caricato`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (slot) => {
    const updated = await api.deleteFile(job.id, slot);
    setJob(updated);
    toast.message("File rimosso");
  };

  const onRename = async (slot, name) => {
    try {
      const updated = await api.renameFile(job.id, slot, name);
      setJob(updated);
      toast.success("Nome aggiornato");
    } catch (e) { toast.error("Errore rinomina"); }
  };

  const confirmTable = async ({ name, columns, rows }) => {
    try {
      const updated = await api.addTable(job.id, name, columns, rows);
      setJob(updated);
      setPdfPreview(null);
      toast.success("Tabella importata dal PDF");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore importazione"); }
  };

  return (
    <section data-testid="data-sources-section">
      <div className="flex items-center gap-3 mb-4">
        <span className="stage-num">1</span>
        <h2 className="text-xl font-head font-bold">Sorgenti dati</h2>
        <span className="text-sm text-muted-foreground">({job.files.length}/3)</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {job.files.map((f) => <FileCard key={f.slot} file={f} onDelete={onDelete} onRename={onRename} onFilter={setFilterFile} />)}

        {canAdd && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer.files[0]); }}
            data-testid="dropzone"
            className={`rounded-xl border-2 border-dashed p-6 flex flex-col items-center justify-center text-center transition-colors min-h-[160px] ${dragOver ? "border-[hsl(var(--neon))] bg-[hsl(var(--neon)/0.08)]" : "border-border metallic"}`}
          >
            <FilePlus size={30} weight="duotone" className="text-primary mb-2" />
            <p className="text-sm font-medium">Trascina un file qui</p>
            <p className="text-xs text-muted-foreground mb-3">xls · xlsx · csv · pdf</p>
            <input ref={inputRef} type="file" accept=".csv,.xls,.xlsx,.xlsm,.pdf" hidden
              onChange={(e) => doUpload(e.target.files[0])} data-testid="file-input" />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => inputRef.current?.click()} disabled={uploading}
                data-testid="browse-file-btn" className="gap-2">
                <UploadSimple size={16} /> {uploading ? "..." : "Sfoglia"}
              </Button>
              <PasteDialog jobId={job.id} onDone={setJob} />
            </div>
          </div>
        )}
      </div>

      {sheetPrompt && (
        <SheetDialog prompt={sheetPrompt}
          onPick={(s) => doUpload(sheetPrompt.file, s)}
          onCancel={() => setSheetPrompt(null)} />
      )}

      {pdfPreview && (
        <PdfPreviewDialog data={pdfPreview} onConfirm={confirmTable} onCancel={() => setPdfPreview(null)} />
      )}

      {filterFile && (
        <SourceFilterDialog jobId={job.id} file={job.files.find((f) => f.slot === filterFile.slot) || filterFile}
          onSaved={setJob} onClose={() => setFilterFile(null)} />
      )}
    </section>
  );
}
